import { zodResolver } from "@hookform/resolvers/zod";
import { Button, ErrorContainer, Form, FormControl, FormField, FormInput, FormItem, FormMessage, IconBin, IconInputSearch, Text } from "@sentricmusic/ui";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import type { BackendAlbumResponse } from "@/features/songs/spotify/backendMappers";

import { useGetMyAccounts } from "@/api/queries";
import { apiCall } from "@/api/shared";
// @ts-expect-error - Legacy JSX component without types
import ChooseAccountModal from "@/components/ChooseAccountModal";
import { mapBackendAlbum, mapBackendTrack } from "@/features/songs/spotify/backendMappers";
import { useUIStateStore } from "@/store/uiStateStore";
import { isValidSpotifyAlbumUrl } from "@/utils/spotifyUrlUtils";

import type { SpotifyAlbumInfoResponse } from "../types/spotify";

import useMultiSongStore from "../store/multiSongStore";
import { useWizardStore } from "../store/wizardStore";

/**
 * Step 1 for the "Add Album" flow.
 *
 * Handles:
 * - Spotify album URL input + validation (react-hook-form + zod)
 * - Deep-link auto-fetch via `?spotifyAlbumId=` query param
 * - Album + track fetch (including ISRCs) via backend SpotifyController
 * - Spotify embed iframe preview
 * - Account-selection modal when deep-linking with multiple accounts
 *
 * The parent orchestrator (SongsAddMultiple) renders WizardNavigation separately
 * and reads sourceData/tracks from multiSongStore to determine when to enable "Next".
 */
export default function AlbumImportStep() {
  const { t } = useTranslation(["album", "songs"]);
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const location = useRouterState({ select: s => s.location });

  const {
    setSourceData,
    setTracks,
    setIsLoading,
    setError: setErrorMsg,
    isLoading,
    error: errorMsg,
  } = useMultiSongStore();

  const { currentStep, markStepComplete, nextStep } = useWizardStore();

  // --- Local state ---
  const [spotifyURL, setSpotifyURL] = useState("");
  const [spotifyResult, setSpotifyResult] = useState<SpotifyAlbumInfoResponse | null>(null);
  const [showIframeSkeleton, setShowIframeSkeleton] = useState(false);
  const [_albumName, setAlbumName] = useState<string | null>(null);

  const hasLoadedFromUrl = useRef(false);

  // --- Zod form ---
  const spotifyAlbumSchema = z.object({
    spotifyUrl: z
      .string()
      .min(1, t("album:enter-spotify-album-url", "Please enter a Spotify album URL"))
      .refine(
        url => isValidSpotifyAlbumUrl(url),
        { message: t("album:enter-valid-spotify-album-url", "Please enter a valid Spotify album URL") },
      ),
  });

  type SpotifyAlbumFormData = { spotifyUrl: string };

  const form = useForm<SpotifyAlbumFormData>({
    resolver: zodResolver(spotifyAlbumSchema),
    defaultValues: { spotifyUrl: "" },
  });

  // --- Account selection for deep links ---
  const myAccounts = useGetMyAccounts();
  const [showAccountModal, setShowAccountModal] = useState(false);
  const hasCheckedDeepLinkAccount = useRef(false);

  useEffect(() => {
    if (hasCheckedDeepLinkAccount.current || !myAccounts.isSuccess || !Array.isArray(myAccounts.data?.artistaccounts)) {
      return;
    }
    const searchParams = new URLSearchParams(location.searchStr);
    const spotifyAlbumId = searchParams.get("spotifyAlbumId");

    if (spotifyAlbumId != null && spotifyAlbumId !== "" && myAccounts.data.artistaccounts.length > 1) {
      hasCheckedDeepLinkAccount.current = true;
      setShowAccountModal(true);
    }
    else {
      hasCheckedDeepLinkAccount.current = true;
    }
  }, [location.searchStr, myAccounts.isSuccess, myAccounts.data?.artistaccounts]);

  // --- Iframe skeleton timer ---
  useEffect(() => {
    if (showIframeSkeleton) {
      const timer = setTimeout(() => setShowIframeSkeleton(false), 1800);
      return () => clearTimeout(timer);
    }
  }, [showIframeSkeleton]);

  // --- Helpers ---
  const parseSpotifyUrl = (url: string): string | null => {
    const urlParts = url.split("/");
    const albumId = urlParts[urlParts.length - 1].split("?")[0];
    return albumId && albumId !== "" ? albumId : null;
  };

  // --- Core fetch logic ---
  const fetchAlbumData = async (url?: string) => {
    const urlToUse = url ?? spotifyURL;
    if (!urlToUse || urlToUse.trim() === "")
      return;

    setIsLoading(true);
    setErrorMsg(null);

    try {
      const albumId = parseSpotifyUrl(urlToUse);

      if (albumId == null) {
        throw new Error(t("album:could-not-parse-album-id", "Could not parse Album ID from URL."));
      }

      const response = await apiCall({
        endpoint: "/rightsapp/get",
        path: `/api/v1/spotify/album/${albumId}`,
      }) as BackendAlbumResponse;

      const albumInfoData = mapBackendAlbum(response.album);
      const tracks = (response.album.tracks ?? []).map(mapBackendTrack);

      setAlbumName(albumInfoData.name);
      setSourceData(albumInfoData);
      setSpotifyResult(albumInfoData);
      setTracks(tracks);
      setShowIframeSkeleton(true);
    }
    catch (e) {
      console.error("Failed to fetch album data:", e);
      if (e instanceof Error) {
        setErrorMsg(t("album:fetch-album-error-with-message", { defaultValue: "Failed to fetch album data: {{message}}", message: e.message }));
      }
      else {
        setErrorMsg(t("album:fetch-album-error-unknown", "Failed to fetch album data due to an unknown error."));
      }
      setAlbumName(null);
      setSourceData(null);
      setTracks([]);
    }
    finally {
      setIsLoading(false);
    }
  };

  // --- Deep-link auto-fetch (spotifyAlbumId query param) ---
  useEffect(() => {
    if (spotifyResult || hasLoadedFromUrl.current || currentStep !== "import")
      return;

    const searchParams = new URLSearchParams(location.searchStr);
    const spotifyAlbumId = searchParams.get("spotifyAlbumId");
    if (spotifyAlbumId == null || spotifyAlbumId.trim() === "")
      return;

    hasLoadedFromUrl.current = true;

    const isValidSpotifyAlbumId = /^[\da-z]{22}$/i.test(spotifyAlbumId);
    if (!isValidSpotifyAlbumId) {
      setErrorMsg(
        t("album:invalid-spotify-album-id", {
          defaultValue: "Invalid Spotify album ID. Expected 22 characters, got {{length}}. Please check the link or enter a Spotify album URL manually.",
          length: spotifyAlbumId.length,
        }),
      );
      return;
    }

    const fetchAlbumFromUrl = async () => {
      try {
        setIsLoading(true);
        setErrorMsg(null);

        const response = await apiCall({
          endpoint: "/rightsapp/get",
          path: `/api/v1/spotify/album/${spotifyAlbumId}`,
        }) as BackendAlbumResponse;

        const albumInfoData = mapBackendAlbum(response.album);
        const tracks = (response.album.tracks ?? []).map(mapBackendTrack);

        setSourceData(albumInfoData);
        setTracks(tracks);
        setSpotifyResult(albumInfoData);
        setAlbumName(albumInfoData.name);
        setSpotifyURL(`https://open.spotify.com/album/${spotifyAlbumId}`);
        setShowIframeSkeleton(true);

        toast.success(
          t("album:album-loaded-from-spotify", {
            defaultValue: "Album loaded from Spotify: \"{{name}}\" ({{count}} tracks)",
            name: albumInfoData.name,
            count: tracks.length,
          }),
        );

        markStepComplete("import");
        nextStep();
      }
      catch (error) {
        console.error("Error fetching album from URL:", error);
        setErrorMsg(
          error instanceof Error
            ? t("album:fetch-album-error-with-message", { defaultValue: "Failed to fetch album data: {{message}}", message: error.message })
            : t("album:fetch-album-error-fallback", "Could not load album from Spotify. Please try entering the Spotify URL manually."),
        );
        toast.error(t("album:fetch-album-error-toast", "Failed to load album from Spotify. Please try again."));
      }
      finally {
        setIsLoading(false);
      }
    };

    void fetchAlbumFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.searchStr, spotifyResult, currentStep]);

  const onFormSubmit = async (data: SpotifyAlbumFormData) => {
    setSpotifyURL(data.spotifyUrl);
    const albumId = parseSpotifyUrl(data.spotifyUrl);
    if (albumId === null || albumId === "") {
      form.setError("spotifyUrl", {
        type: "manual",
        message: "Invalid Spotify album URL. Please check the URL and try again.",
      });
      return;
    }
    await fetchAlbumData(data.spotifyUrl);
  };

  const removeSpotify = () => {
    setSpotifyURL("");
    setSpotifyResult(null);
    setSourceData(null);
    setTracks([]);
    setErrorMsg(null);
    setShowIframeSkeleton(false);
    form.reset();
  };

  return (
    <>
      <div
        className={`flex flex-col gap-6 shadow-md ${isTunecore ? "text-white md:px-14 md:py-10 p-4 rounded-lg bg-layer-inverse" : "bg-layer-raised p-4 md:px-14 md:py-10 rounded-lg text-ink-primary"}`}
        id="albumSpotifyStep"
      >
        <div>
          <Text as="subtitle">
            {t("album:import-from-spotify-header")}
          </Text>
          <Text>
            {t("album:spotify-album-import-description")}
            {" "}
            <span>
              <a
                className="underline hover:scale-105"
                href="https://support.spotify.com/us/artists/article/finding-your-artist-url/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("album:how-to-find-album-url")}
              </a>
            </span>
          </Text>
        </div>
        <div>
          {!spotifyResult && (
            <Form {...form}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void form.handleSubmit(onFormSubmit)(e);
                }}
                className="flex flex-col gap-4"
              >
                <FormField
                  control={form.control}
                  name="spotifyUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <FormInput
                          type="text"
                          placeholder={t("album:paste-spotify-album-url", "Paste Spotify Album URL here")}
                          className="w-full font-medium "
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  className={`self-end group ${
                    isTunecore
                      ? "text-white font-tunecore hover:text-ink-accent"
                      : "bg-[#1ED760] hover:bg-[#1DB954]  text-black"
                  }`}
                  type="submit"
                  variant="darkSecondary"
                  icon={<IconInputSearch className={`${isTunecore ? "stroke-white group-hover:stroke-ink-accent" : "stroke-black"}`} />}
                  disabled={isLoading}
                  label={isLoading ? t("album:fetching-album") : t("album:fetch-spotify-album")}
                />
              </form>
            </Form>
          )}
          {spotifyResult && (
            <div className="flex flex-col gap-4">
              <div className="relative w-full min-h-[400px]">
                {showIframeSkeleton
                  ? (
                      <div className="w-full min-h-[400px] bg-black/10 backdrop-blur-xl rounded-lg animate-pulse flex flex-col">
                        <div className="flex items-center gap-4 p-6">
                          <div className="w-20 h-20 bg-black/10 rounded-md backdrop-blur-xl animate-pulse" />
                          <div className="flex-1">
                            <div className="w-3/4 h-4 mb-2 bg-black/10 rounded-md backdrop-blur-xl animate-pulse" />
                            <div className="w-1/2 h-3 bg-black/10 rounded-md backdrop-blur-xl animate-pulse" />
                          </div>
                        </div>
                        <div className="flex-1 p-6 pt-0">
                          <div className="space-y-6">
                            {Array.from({ length: 4 }, (_, index) => (
                              <div key={`track-skeleton-${index}`} className="flex items-center gap-4">
                                <div className="w-6 h-4 bg-black/10 rounded backdrop-blur-xl animate-pulse" />
                                <div className="flex-1 h-4 bg-black/10 rounded backdrop-blur-xl animate-pulse" />
                                <div className="w-12 h-4 bg-black/10 rounded backdrop-blur-xl animate-pulse" />
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg">
                          <div className="flex items-center gap-2 font-medium text-ink-primary">
                            <div className="w-4 h-4 border-2 border-current rounded-full border-t-transparent animate-spin" />
                            {t("album:loading-spotify-preview")}
                          </div>
                        </div>
                      </div>
                    )
                  : (
                      <iframe
                        title="preview"
                        src={`https://open.spotify.com/embed/album/${spotifyResult.id}`}
                        height="152"
                        allow="encrypted-media"
                        className="w-full min-h-[400px] rounded-xl"
                        sandbox="allow-scripts"
                      />
                    )}
              </div>
              <Button
                variant="darkSecondary"
                onClick={removeSpotify}
                className={`self-end group ${
                  isTunecore
                    ? "text-white font-tunecore hover:text-ink-accent"
                    : "bg-[#1ED760] hover:bg-[#1DB954]  text-black"
                }`}
                icon={<IconBin className={`${isTunecore ? "stroke-white group-hover:stroke-ink-accent" : "stroke-black"}`} />}
                label={t("album:remove-selection")}
              />
            </div>
          )}
        </div>
      </div>

      {errorMsg !== null && errorMsg !== "" && (
        <ErrorContainer
          className="mt-4"
          message={errorMsg}
        />
      )}

      {/* Account Selection Modal for Deep Links */}
      <ChooseAccountModal
        open={showAccountModal}
        setAccountChoiceModal={setShowAccountModal}
        accounts={myAccounts.data?.artistaccounts}
        to={location.pathname + location.searchStr}
        title={t("album:select-account-for-album", "Select the account you want to add this album to:")}
      />
    </>
  );
}
